import { addNotification } from 'containers/Notifications/notificationsSlice'
import { selectUserId } from 'containers/Login/loginSlice'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import useTranslation from 'hooks/useTranslation'
import { LOCATION_STATES } from 'utils/constants'
import { useState } from 'react'
import { v7 } from 'uuid'
import {
  SAVED_METHOD_POLL_TIMEOUT,
  awaitStatus,
  buildTokenExtra,
  getMerchantLabel,
  getWalletAdapter,
  hasCardAdapter,
  isWalletNative,
  resolveWalletPosId,
  resolveWalletSandbox,
  savePaymentReturn,
  settle,
  toAmount
} from 'payments'

const usePaymentProcessing = ({
  selected,
  getInvoices,
  amount,
  paymentMethods,
  userId: selectedUserId,
  /** dokąd wrócić z /payment/status po bramce / 3DS (np. "Ponów płatność" po porażce) */
  returnPath,
  close
}) => {
  const [bankTransfer, setBankTransfer] = useState(null)
  const [isLoading, setLoading] = useState(false)
  const _userId = useSelector(selectUserId)
  const { pathname } = useLocation()

  const userId = selectedUserId || _userId

  const dispatch = useDispatch()
  const { push } = useHistory()
  const { t } = useTranslation('payment')

  const getPayment = () => {
    const payment = {
      amount,
      methodId: selected.methodId,
      key: selected.key,
      date: 'now'
    }
    if (selected.optionId) {
      payment.optionId = selected.optionId
    }
    return payment
  }

  /**
   * @param {string} localPaymentId
   * @param {object|string} extra { code } | { cardToken } | { walletToken }
   */
  const generateBody = async (localPaymentId, extra) => {
    const payment = getPayment()
    const invoices = await getInvoices(payment)
    payment.init = true
    payment.localPaymentId = localPaymentId

    if (selected.supportsRecurring && selected.saveForRecurring) {
      payment.saveForRecurring = true
    }

    // kompatybilność wstecz: wcześniej drugim argumentem był sam kod BLIK
    const additional = typeof extra === 'string'
      ? (extra ? { code: extra } : {})
      : (extra || {})
    Object.assign(payment, additional)

    return {
      cartId: null,
      action: 'payment',
      userId,
      invoices,
      payments: [payment]
    }
  }

  /** Jedna reakcja na wynik rozliczenia - wspólna dla wszystkich metod. */
  const handleResult = (result, localPaymentId) => {
    if (!result) {
      setLoading(false)
      return false
    }

    switch (result.type) {
      case 'bankTransfer':
        setBankTransfer(result.bankTransfer)
        setLoading(false)
        return true

      case 'redirect':
        if (returnPath) savePaymentReturn(returnPath, 'invoice')
        window.open(result.url, '_self')
        setTimeout(() => {
          setLoading(false)
        }, 500)
        return true

      // wpłata przyjęta, operator jeszcze nie potwierdził (zapisany alias BLIK - nawet do 72 h);
      // zamykamy ekran z komunikatem, lista faktur odświeży się po powrocie
      case 'pending':
        dispatch(addNotification({ type: 'info', translate: false, message: t('waitingForBankConfirmation') }))
        setLoading(false)
        close?.(result.remotePaymentId, localPaymentId)
        return true

      case 'success':
        setLoading(false)
        close?.(result.remotePaymentId, localPaymentId)
        return true

      default:
        setLoading(false)
        if (result.message && !result.handled) {
          dispatch(addNotification({ type: 'error', message: result.message, translate: false }))
        }
        return false
    }
  }

  const initOnlinePayment = async () => {
    const localPaymentId = v7()
    const body = await generateBody(localPaymentId, {})
    if (!body) return false

    // zapisany alias BLIK potrafi czekać na bank do 72 h - nie trzymamy klienta w spinnerze
    const options = selected.key === 'saved_method'
      ? { timeoutMs: SAVED_METHOD_POLL_TIMEOUT }
      : undefined

    const result = await settle(body, options)
    return handleResult(result, localPaymentId)
  }

  /** Apple Pay / Google Pay w trybie native. */
  const payWithWallet = async () => {
    const walletForm = selected.walletForm
    const adapter = getWalletAdapter(walletForm)
    if (!adapter) {
      setLoading(false)
      return false
    }

    const localPaymentId = v7()
    const options = {
      amount: toAmount(amount),
      label: getMerchantLabel(),
      sandbox: resolveWalletSandbox(walletForm, paymentMethods),
      posId: resolveWalletPosId(walletForm, paymentMethods)
    }

    try {
      if (walletForm.wallet === 'applepay') {
        let settled = null
        await adapter.requestToken(walletForm, {
          ...options,
          // arkusz Apple nie doczekałby odpytywania o status - w środku leci samo rozliczenie
          onAuthorized: async (token) => {
            const body = await generateBody(localPaymentId, buildTokenExtra(selected, token))
            settled = await settle(body, { poll: false })
            return settled.type !== 'error' && settled.type !== 'failure'
          }
        })

        if (settled?.type === 'pending') {
          settled = await awaitStatus(settled.remotePaymentId)
        }
        return handleResult(settled, localPaymentId)
      }

      const token = await adapter.requestToken(walletForm, options)
      const body = await generateBody(localPaymentId, buildTokenExtra(selected, token))
      const result = await settle(body)
      return handleResult(result, localPaymentId)

    } catch (error) {
      // token portfela jest jednorazowy - po błędzie klient otwiera arkusz od nowa
      setLoading(false)
      if (error?.code === 'wallet_cancelled') return false
      console.error(error)
      if (error?.message && error.message !== error?.code) {
        dispatch(addNotification({ type: 'error', message: error.message, translate: false }))
      }
      return false
    }
  }

  const purchase = async () => {
    if (isLoading) return
    setLoading(true)

    switch (selected.key) {
      case 'blik':
        push(pathname, LOCATION_STATES.popup({ type: 'blik', payment: getPayment() }))
        break

      case 'online_card':
        if (hasCardAdapter(selected.cardForm)) {
          push(pathname, LOCATION_STATES.popup({
            type: 'card',
            payment: getPayment(),
            cardForm: selected.cardForm,
            saveForRecurring: !!(selected.supportsRecurring && selected.saveForRecurring)
          }))
        } else {
          initOnlinePayment()
        }
        break

      case 'applepay':
      case 'gpay':
        if (isWalletNative(selected)) {
          payWithWallet()
        } else {
          initOnlinePayment()
        }
        break

      default:
        initOnlinePayment()
    }
  }

  return {
    isProcessing: isLoading,
    bankTransfer,
    purchase,
    setLoading,
    generateBody,
    handleResult,
    clearBankTransfer: () => {
      setBankTransfer(null)
    }
  }
}

export default usePaymentProcessing
